import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useCountry } from "../context/CountryContext";

export default function CountrySelectorPage() {
  const navigate = useNavigate();
  const { setCountry, hasSelected } = useCountry();

  // If already selected, skip to home
  useEffect(() => {
    if (hasSelected) {
      navigate("/home", { replace: true });
    }
  }, [hasSelected, navigate]);

  const handleSelect = (code) => {
    setCountry(code);
    navigate("/home", { replace: true });
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-gradient-to-br from-[#020617] via-[#071032] to-[#03040b] text-white flex flex-col items-center justify-center px-4 py-4 relative">

      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl" />
        {/* Extra blob for Namibia */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md flex flex-col items-center text-center">

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-4"
        >
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-violet-500 flex items-center justify-center shadow-2xl mx-auto">
            <img src="/banklogo.png" alt="logo" className="w-9 h-9" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2"
        >
          Welcome to{" "}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-violet-400">
            ProBank
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-sm text-slate-400 mb-6"
        >
          Choose your country to continue
        </motion.p>

        {/* Country Buttons */}
        <div className="w-full space-y-3">

          {/* Top row: Nigeria + Ghana */}
          <div className="grid grid-cols-2 gap-3">

            {/* Nigeria */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleSelect("NG")}
              className="relative bg-gradient-to-br from-green-600/20 to-green-900/40 hover:from-green-600/30 hover:to-green-900/50 border border-green-500/40 rounded-2xl p-5 transition-all shadow-lg"
            >
              <div className="text-5xl mb-2">🇳🇬</div>
              <div className="text-lg font-bold text-white">Nigeria</div>
              <div className="text-xs text-green-300 mt-0.5">₦ Naira</div>
            </motion.button>

            {/* Ghana */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleSelect("GH")}
              className="relative bg-gradient-to-br from-yellow-600/20 to-red-900/40 hover:from-yellow-600/30 hover:to-red-900/50 border border-yellow-500/40 rounded-2xl p-5 transition-all shadow-lg"
            >
              <div className="text-5xl mb-2">🇬🇭</div>
              <div className="text-lg font-bold text-white">Ghana</div>
              <div className="text-xs text-yellow-300 mt-0.5">₵ Cedi</div>
            </motion.button>
          </div>

          {/* Bottom row: Namibia — full width */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => handleSelect("NA")}
            className="relative w-full bg-gradient-to-br from-blue-600/20 to-sky-900/40 hover:from-blue-600/30 hover:to-sky-900/50 border border-blue-400/40 rounded-2xl p-4 transition-all shadow-lg flex items-center justify-center gap-4"
          >
            {/* Flag + info side by side since it's full width */}
            <div className="text-5xl">🇳🇦</div>
            <div className="text-left">
              <div className="text-lg font-bold text-white">Namibia</div>
              <div className="text-xs text-blue-300 mt-0.5">N$ Namibian Dollar · Pay in USD</div>
            </div>
            {/* NEW badge */}
            <div className="absolute top-2 right-3 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              NEW
            </div>
          </motion.button>
        </div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-xs text-slate-500 mt-5"
        >
          You can switch countries later in settings
        </motion.p>
      </div>

      {/* Footer */}
      <div className="absolute bottom-3 left-0 right-0 text-center text-[10px] text-slate-600 z-10">
        Secure · Verified · Trusted
      </div>
    </div>
  );
}
